import { Component, Input, OnInit, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';
import Swal from 'sweetalert2';
import { SectoresService } from '../services/sectores.service';
import { IFinanzaItem } from '../interfaces/sector.interface';
import { SessionService } from '@shared/helpers/sesion.service';

@Component({
  selector: 'app-finanzas',
  templateUrl: './finanzas.component.html',
  providers: [MessageService]
})
export class FinanzasComponent implements OnInit, OnChanges {
  @Input() sector: string = '';
  @Input() descripcion: string = '';
  @Input() visible: boolean = false;

  @ViewChild('dt') table!: Table;
  
  dataSource: IFinanzaItem[] = [];
  loading: boolean = false;
  first: number = 0;
  rows: number = 10;
  totalRecords: number = 0;
  displayFormDialog: boolean = false;
  isEditMode: boolean = false;
  itemToEdit: IFinanzaItem | null = null;

  displayedColumns = [
    { field: 'desde', header: 'Desde', sortable: true },
    { field: 'hasta', header: 'Fecha Hasta', sortable: true },
    { field: 'tipo', header: 'Tipo Estado', sortable: true },
    { field: 'variableFinanciera', header: 'Variable Financiera', sortable: true },
    { field: 'valorMinimo', header: 'Valor Mín.', sortable: true },
    { field: 'valorMaximo', header: 'Valor Máx.', sortable: true },
    { field: 'valorPromedio', header: 'Valor Prom.', sortable: true },
    { field: 'responsable', header: 'Responsable', sortable: true },
    { field: 'fechaAlta', header: 'Fecha Alta', sortable: true },
    { field: 'actions', header: 'Acciones', sortable: false }
  ];

  constructor(
    private messageService: MessageService,
    private sectoresService: SectoresService,
    private sessionService: SessionService
  ) {}

  ngOnInit(): void {
    // No cargar datos automáticamente al inicializar
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Solo cargar datos cuando el componente se vuelve visible y tiene un sector válido
    if (changes['visible'] && changes['visible'].currentValue === true && this.sector) {
      this.loadData();
    }
  }

  loadData(): void {
    this.loading = true;
    
    const request = {
      sUsuario: this.sessionService.getUserSession() || '',
      que: 4,
      tope: '9999',
      sector: this.sector,
      sDesde: '',
      sHasta: '',
      tipoEstado: '',
      variableFinanciera: 0,
      valorMinimo: 0,
      valorMaximo: 0,
      valorPromedio: 0
    };

    this.sectoresService.getFinanzas(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.dataSource = response.data;
          this.totalRecords = response.data.length;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los datos de finanzas'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error al cargar finanzas:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los datos de finanzas'
        });
        this.loading = false;
      }
    });
  }

  onPage(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
    this.loadData();
  }

  openNew(): void {
    this.isEditMode = false;
    this.itemToEdit = null;
    this.displayFormDialog = true;
  }

  onFormSaved(result: any): void {
    console.log('Resultado del formulario:', result);
    
    if (result.success) {
      this.messageService.add({ 
        severity: 'success', 
        summary: 'Éxito', 
        detail: result.message 
      });
      this.displayFormDialog = false;
      this.isEditMode = false;
      this.itemToEdit = null;
      this.loadData(); // Recargar la tabla
    } else {
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Error', 
        detail: result.message 
      });
    }
  }

  onFormCancelled(): void {
    this.displayFormDialog = false;
    this.isEditMode = false;
    this.itemToEdit = null;
  }

  openEdit(item: IFinanzaItem): void {
    this.itemToEdit = item;
    this.isEditMode = true;
    this.displayFormDialog = true;
  }

  openDelete(item: IFinanzaItem): void {
    Swal.fire({
      title: '¿Está seguro?',
      text: `¿Está seguro que desea eliminar la finanza del sector ${item.sector}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      iconColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteFinanza(item);
      }
    });
  }

  deleteFinanza(item: IFinanzaItem): void {
    this.loading = true;
    
    const userSession = this.sessionService.getUserSession();
    const sUsuario = userSession && userSession.trim() !== '' ? userSession : 'admin';
    
    const request = {
      sUsuario: sUsuario,
      que: 3, // Borrar
      tope: '',
      sector: item.sector,
      sDesde: item.desde,
      sHasta: item.hasta,
      tipoEstado: item.tipo,
      variableFinanciera: item.variableFinanciera,
      valorMinimo: item.valorMinimo,
      valorMaximo: item.valorMaximo,
      valorPromedio: item.valorPromedio
    };

    console.log('Request Delete Finanza:', request);

    this.sectoresService.deleteFinanza(request).subscribe({
      next: (response) => {
        console.log('Response Delete Finanza:', response);
        if (response.success && response.data) {
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Éxito', 
            detail: 'Finanza eliminada correctamente' 
          });
          this.loadData(); // Recargar la tabla
        } else {
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Error al eliminar la finanza' 
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error al eliminar finanza:', error);
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Error al eliminar la finanza' 
        });
        this.loading = false;
      }
    });
  }
}
